// Push Notification Utilities

const VAPID_PUBLIC_KEY = 'BEl62iUYgUivxIkv69yViEuiBIa-Ib9-SkvMeAtA3LFgDzkrxZJjSgSnfckjBJuBkr3qBUYIHBQFLXYp5Nksh8U';

// Check if push notifications are supported
export const isPushSupported = () => {
  return 'serviceWorker' in navigator && 'PushManager' in window && 'Notification' in window;
};

// Get notification permission status
export const getNotificationPermission = () => {
  if (!isPushSupported()) return 'unsupported';
  return Notification.permission;
};

// Request notification permission
export const requestNotificationPermission = async () => {
  if (!isPushSupported()) {
    console.log('Push notifications not supported');
    return 'unsupported';
  }
  
  try {
    const permission = await Notification.requestPermission();
    console.log('Notification permission:', permission);
    return permission;
  } catch (error) {
    console.error('Error requesting notification permission:', error);
    return 'denied';
  }
};

// Register service worker
export const registerServiceWorker = async () => {
  if (!('serviceWorker' in navigator)) {
    console.log('Service workers not supported');
    return null;
  }
  
  try {
    const registration = await navigator.serviceWorker.register('/sw.js');
    console.log('Service Worker registered:', registration);
    return registration;
  } catch (error) {
    console.error('Service Worker registration failed:', error);
    return null;
  }
};

// Subscribe to push notifications
export const subscribeToPush = async () => {
  try {
    const registration = await navigator.serviceWorker.ready;
    
    // Check for existing subscription
    let subscription = await registration.pushManager.getSubscription();
    
    if (!subscription) {
      // Create new subscription
      subscription = await registration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(VAPID_PUBLIC_KEY)
      });
      console.log('Push subscription created:', subscription);
    }
    
    return subscription;
  } catch (error) {
    console.error('Failed to subscribe to push:', error);
    return null;
  }
};

// Unsubscribe from push notifications
export const unsubscribeFromPush = async () => {
  try {
    const registration = await navigator.serviceWorker.ready;
    const subscription = await registration.pushManager.getSubscription();
    
    if (subscription) {
      await subscription.unsubscribe();
      console.log('Unsubscribed from push notifications');
      return true;
    }
    return false;
  } catch (error) {
    console.error('Failed to unsubscribe:', error);
    return false;
  }
};

// Send local notification (for testing and in-app notifications)
export const showLocalNotification = async (title, options = {}) => {
  if (Notification.permission !== 'granted') {
    console.log('Notification permission not granted');
    return false;
  }
  
  try {
    const registration = await navigator.serviceWorker.ready;
    
    await registration.showNotification(title, {
      body: options.body || '',
      icon: options.icon || '/logo192.png',
      badge: '/logo192.png',
      tag: options.tag || 'local-notification',
      data: options.data || {},
      vibrate: [200, 100, 200],
      requireInteraction: options.requireInteraction || false,
      actions: options.actions || []
    });
    
    return true;
  } catch (error) {
    console.error('Failed to show notification:', error);
    return false;
  }
};

// Helper function to convert VAPID key
function urlBase64ToUint8Array(base64String) {
  const padding = '='.repeat((4 - base64String.length % 4) % 4);
  const base64 = (base64String + padding)
    .replace(/-/g, '+')
    .replace(/_/g, '/');
  
  const rawData = window.atob(base64);
  const outputArray = new Uint8Array(rawData.length);
  
  for (let i = 0; i < rawData.length; ++i) {
    outputArray[i] = rawData.charCodeAt(i);
  }
  return outputArray;
}

// Notification types for the app
export const NotificationTypes = {
  RESERVATION_CONFIRMED: 'reservation_confirmed',
  RESERVATION_CANCELLED: 'reservation_cancelled',
  RESERVATION_REMINDER: 'reservation_reminder',
  PAYMENT_RECEIVED: 'payment_received',
  PACKAGE_EXPIRING: 'package_expiring',
  NEW_MESSAGE: 'new_message',
  QR_SCANNED: 'qr_scanned'
};

// Create notification content based on type
export const createNotificationContent = (type, data = {}) => {
  switch (type) {
    case NotificationTypes.RESERVATION_CONFIRMED:
      return {
        title: '✅ Rezervasyon Onaylandı',
        body: `${data.date} tarihli ${data.time} saatindeki dersiniz onaylandı.`,
        tag: 'reservation',
        data: { url: '/dashboard/reservations', ...data }
      };
    
    case NotificationTypes.RESERVATION_CANCELLED:
      return {
        title: '❌ Rezervasyon İptal Edildi',
        body: `${data.date} tarihli ${data.time} saatindeki dersiniz iptal edildi.`,
        tag: 'reservation',
        data: { url: '/dashboard/reservations', ...data }
      };
    
    case NotificationTypes.RESERVATION_REMINDER:
      return {
        title: '⏰ Ders Hatırlatması',
        body: `${data.time} saatinde ${data.branch} dersiniz var. Hazır olun!`,
        tag: 'reminder',
        requireInteraction: true,
        data: { url: '/dashboard', ...data }
      };
    
    case NotificationTypes.PAYMENT_RECEIVED:
      return {
        title: '💳 Ödeme Alındı',
        body: `${data.amount} TL tutarında ödemeniz alındı.`,
        tag: 'payment',
        data: { url: '/dashboard/payments', ...data }
      };
    
    case NotificationTypes.PACKAGE_EXPIRING:
      return {
        title: '📦 Paket Bitiyor',
        body: `Paketinizin bitimine ${data.days} gün kaldı. ${data.sessions} seans hakkınız var.`,
        tag: 'package',
        data: { url: '/dashboard/my-packages', ...data }
      };
    
    case NotificationTypes.NEW_MESSAGE:
      return {
        title: '💬 Yeni Mesaj',
        body: `${data.sender}: ${data.preview}`,
        tag: 'message',
        data: { url: '/dashboard/messages', ...data }
      };
    
    case NotificationTypes.QR_SCANNED:
      return {
        title: '✅ Giriş Onaylandı',
        body: `Hoş geldiniz! Dersinize başlayabilirsiniz.`,
        tag: 'qr',
        data: { url: '/dashboard', ...data }
      };
    
    default:
      return {
        title: 'PBStudio',
        body: data.message || 'Yeni bir bildiriminiz var',
        tag: 'default',
        data: { url: '/dashboard', ...data }
      };
  }
};
